import { Button3D, CallContainer, UserCallData as EscaetUser, ReactIcons } from "@kalyzee/escaet-ui";
import { Loading, useParams } from "@kalyzee/kast-web-sdk/web-components";
import { MeetingCallState, MeetingCreateUserData, MeetingState, MeetingUserDataCallStatus } from "@kalyzee/kast-web-sdk/websocket";
import { useEffect, useRef, useState } from "react";
import { PageMasterMode, usePageContextRef } from "../../../../components/navigation/PageContext";
import { getEscaetUserCallData } from "../../../../helpers/meeting";
import { toastError } from "../../../../helpers/toast";
import { useSocketAppDispatch } from "../../../../hooks/app";
import { useMeetingSelector, useFocusedMeetingAPI } from "../../../../hooks/meeting";
import { SessionMode } from "../../../../store/session/slices";
import { pedagogyExtraUserDataToString } from "../../../../helpers/pedagogy";
import { sdkClient } from "../../../../helpers/sdk";

const MeetingCallExpertPage = () => {
  const pageContextRef = usePageContextRef<SessionMode.MASTER>();
  const dispatch = useSocketAppDispatch();

  const urlParams = useParams();
  const isExitParam = urlParams.get("exit") === "true";

  const [resultMode, setResultMode] = useState(false);

  const meetingCtx = useFocusedMeetingAPI();
  const [meeting] = useMeetingSelector(meetingCtx, (state) => state.meeting);

  const showLaunchBtn = useRef(true);

  const callOngoing = meeting?.callState && meeting.callState !== MeetingCallState.CLOSED;

  useEffect(() => {
    if (callOngoing) {
      showLaunchBtn.current = false;
      // So we need to leave the page and come back after finishing the call
      // before seeing the launch button again
    }
  }, [callOngoing]);

  if (!meeting) return <Loading fullscreen message="Chargement en cours" />;

  if (!callOngoing && showLaunchBtn.current) {
    return (
      <div style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "1em",
      }}>
        <div style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "1em",
        }}>
          <ReactIcons.QRCode style={{ width: 120 }} />
          <div style={{
            width: 800,
            fontSize: "1.5em",
            fontWeight: "500",
          }}>
            Vous vous apprêtez à lancer <b>{isExitParam ? "un appel de sortie" : "un appel d'entrée"}</b>. Les participants en salle devront déclarer leur présence <b>en scannant le QR-Code</b> affiché sur les écrans de fond de salle.
          </div>
        </div>
        <Button3D onClick={async () => {
          const result = await sdkClient.getWebsocketClient().emitter.meeting.startCall({ 
            id: meeting.id,
            isEndCall: isExitParam,
          });
          if (result.error || !result.response) {
            toastError(`Une erreur est survenue lors du lancement de l'appel.`);
            return;
          }
          // TODO : Temp, need to send meeting update event on backend :
          meetingCtx?.setMeeting(result.response.meeting);
        }}>
          Commencer ...
        </Button3D>
      </div>
    );
  } 

  const meetingId = pageContextRef.current?.params?.id;

  const navigateToMeeting = () => {
    pageContextRef.current?.goTo?.(PageMasterMode.MEETING_DASHBOARD, { id: meetingId });
  };

  const { callMultipleEnabled } = meeting;

  // if (!callEnabled || ((call) && !callMultipleEnabled && !endCallEnabled && !resultMode)) {
  //   return <GoTo page={PageMasterMode.MEETING_DASHBOARD} params={{ id: meetingId }} />;
  // }

  const onValidateCall = async (callData: EscaetUser[]) => {
    const mapping = {
      online: MeetingUserDataCallStatus.ONLINE,
      on_site: MeetingUserDataCallStatus.ONSITE,
      missing: MeetingUserDataCallStatus.MISSING,
    };
    const result = await sdkClient.getWebsocketClient().emitter.meeting.endCall({
        id: meeting.id,
        usersData: callData
          .map<MeetingCreateUserData | undefined>((user: EscaetUser) => {
            // kicked cannot be set in the interface, it was here before, it's useless to send it
            if (user.status === "kicked") return undefined;

            const callStatus = user.manualOnSiteStatus ? mapping[user.manualOnSiteStatus] : mapping[user.status];

            const call: MeetingCreateUserData["call"] = {
              extraData: pedagogyExtraUserDataToString({ late: true, report: false }),
              status: callStatus,
              seenByUser: true,
            };
            
            if (user.badBehaviorAction) {
              call.seenByUser = false;
              call.extraData = pedagogyExtraUserDataToString({ late: false, report: true });
              call.message = "Veuillez rectifier votre comportement.";
              if (user.badBehaviorAction === "refuse") {
                call.status = MeetingUserDataCallStatus.KICKED; 
              }
            }

            return {
              userId: user.id,
              call,
            };
          })
          .filter(u => !!u) as MeetingCreateUserData[], // filtering undefined
    });
    if (result.error || !result.response) {
      toastError(`Une erreur est survenue lors de l'envoi des données de l'appel.`);
      return;
    }
    // TODO : Temp, need to send meeting update event on backend :
    meetingCtx?.setMeeting(result.response.meeting);
    setResultMode(true); // so we keep displaying results, otherwise we'd be redirected to dashboard
  };

  const onLaunchCourse = () => {
    // so we leave result mode
    if (callMultipleEnabled) navigateToMeeting();
    setResultMode(false);
  };

  const onStopCourse = async () => {
    const result = await meetingCtx?.updateMeetingState(MeetingState.STOPPED);
    if (result?.error || !result?.response) {
      toastError(`Une erreur est survenue lors de l'arrêt de la session.`);
      return;
    }
    pageContextRef.current?.goTo(PageMasterMode.LIST);
  };

  const escaetUsers = getEscaetUserCallData(meeting);

  return (
    <div className="w-full h-full flex flex-col justify-center items-center bg-gray-100 gap-10 p-10">
      <CallContainer
        users={escaetUsers}
        onLaunchCourse={onLaunchCourse}
        onStopCourse={isExitParam ? onStopCourse : undefined}
        onValidate={onValidateCall}
        onCancel={navigateToMeeting}
        calledByDefault
        isExitCall={meeting.callState === MeetingCallState.OPEN_END}
      />
    </div>
  );
};

export default MeetingCallExpertPage;
